import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ImageDown, Lock, Video, Play, Square, Loader2, Share2, Download } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { canMakeVideo, makeQuoteVideo, videoTiming } from "@/lib/quoteVideo";

export const btn = "text-xs font-mono-ui uppercase tracking-wider px-3 py-1.5 rounded border";
const slug = (q) => q.author.replace(/\W+/g, "-").toLowerCase();
const pick = (on) => `${btn} ${on ? "border-[#FD0F10] text-white" : "border-[#2A2E39] text-gray-400"}`;
let previewAudio = null;

// Android browsers hand blob downloads to the phone's download manager after the click returns,
// so the link must be in the page and the file kept alive for a while.
const saveFile = (file) => {
  const url = URL.createObjectURL(file);
  const a = document.createElement("a");
  a.href = url;
  a.download = file.name;
  a.rel = "noopener";
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { a.remove(); URL.revokeObjectURL(url); }, 120000);
  toast.success("Saved. Look in your Downloads or Gallery.");
};
const canShareFile = (file) => { try { return !!navigator.canShare?.({ files: [file] }); } catch { return false; } };

export const useTracks = (isPro) => {
  const [tracks, setTracks] = useState([]);
  useEffect(() => { if (isPro) api.get("/mindset/music").then((r) => setTracks(r.data.tracks)).catch(() => {}); }, [isPro]);
  return tracks;
};

// Shown once a card or video is ready: on-screen preview (press and hold to save on phones), Share and Save.
const ReadyFile = ({ file, tid }) => {
  const [url, setUrl] = useState("");
  useEffect(() => { const u = URL.createObjectURL(file); setUrl(u); return () => URL.revokeObjectURL(u); }, [file]);
  const isVideo = file.type === "video/mp4";
  const share = async () => {
    try { await navigator.share({ files: [file] }); } catch (e) {
      if (e?.name !== "AbortError") saveFile(file);
    }
  };
  return (
    <div className="space-y-2" data-testid={`ready-${tid}`}>
      {url && (isVideo
        ? <video src={url} controls playsInline muted className="max-h-72 rounded border border-[#2A2E39] bg-black" data-testid={`preview-${tid}`} />
        : <img src={url} alt="Your quote card" className="max-h-72 rounded border border-[#2A2E39]" data-testid={`preview-${tid}`} />)}
      {!isVideo && <p className="text-xs text-gray-400">On a phone you can also press and hold the picture to save it.</p>}
      <div className="flex flex-wrap gap-2">
        {canShareFile(file) && (
          <button onClick={share} className="hero-accent text-white text-xs font-mono-ui uppercase tracking-wider px-4 py-2 rounded flex items-center gap-1.5" data-testid={`share-${tid}`}>
            <Share2 size={13} /> Share to TikTok, Instagram, Facebook
          </button>
        )}
        <button onClick={() => saveFile(file)} className={`${btn} border-[#2A2E39] text-gray-200 flex items-center gap-1.5`} data-testid={`save-${tid}`}>
          <Download size={13} /> Save to phone
        </button>
      </div>
    </div>
  );
};

const Locked = ({ q, what }) => (
  <Link to="/pro" className={`flex items-center gap-1.5 ${btn} text-gray-400 border-[#2A2E39] hover:text-white`} data-testid={`quote-${what}-locked-${q.id}`}>
    <Lock size={13} /> Make a {what} (Pro)
  </Link>
);

const CardPanel = ({ q }) => {
  const [busy, setBusy] = useState("");
  const [file, setFile] = useState(null);
  const make = async (size) => {
    setBusy(size); setFile(null);
    try {
      const r = await api.get(`/mindset/card/${q.id}`, { params: { size }, responseType: "blob" });
      setFile(new File([r.data], `quote-${slug(q)}-${size}.png`, { type: "image/png" }));
    } catch { toast.error("Couldn't make the card. Try again."); } finally { setBusy(""); }
  };
  return (
    <div className="w-full mt-2 bg-[#0B0C0E] border border-[#2A2E39] rounded-lg p-3 space-y-3" data-testid={`quote-card-sizes-${q.id}`}>
      <div className="flex flex-wrap gap-2">
        {[["square", "Square (FB, IG)"], ["tall", "Tall (TikTok, Reels)"]].map(([s, label]) => (
          <button key={s} onClick={() => make(s)} disabled={!!busy} className={`${pick(file?.name.endsWith(`-${s}.png`))} flex items-center gap-1.5`} data-testid={`quote-card-${s}-${q.id}`}>
            {busy === s && <Loader2 size={12} className="animate-spin" />} {label}
          </button>
        ))}
      </div>
      {file && <ReadyFile file={file} tid={`card-${q.id}`} />}
    </div>
  );
};

const VideoPanel = ({ q, tracks }) => {
  const [size, setSize] = useState("tall");
  const [music, setMusic] = useState(tracks[0]?.id || "none");
  const [playing, setPlaying] = useState(false);
  const [busy, setBusy] = useState(false);
  const [file, setFile] = useState(null);
  const stop = () => { previewAudio?.pause(); previewAudio = null; setPlaying(false); };
  useEffect(() => () => previewAudio?.pause(), []);
  const preview = async () => {
    if (playing) return stop();
    try {
      const r = await api.get(`/mindset/music/${music}`, { params: { secs: 10 }, responseType: "blob" });
      previewAudio?.pause();
      previewAudio = new Audio(URL.createObjectURL(r.data));
      previewAudio.onended = () => setPlaying(false);
      previewAudio.play();
      setPlaying(true);
    } catch { toast.error("Couldn't play that track."); }
  };
  const make = async () => {
    stop(); setBusy(true); setFile(null);
    try {
      if (!(await canMakeVideo())) {
        toast.error("This phone's browser can't make videos. Update it, or try the latest Chrome or Safari.");
        return;
      }
      const { data } = await api.get(`/mindset/video/${q.id}`, { params: { size } });
      const secs = videoTiming(data.text).total;
      const audio = music === "none" ? null : (await api.get(`/mindset/music/${music}`, { params: { secs }, responseType: "arraybuffer" })).data;
      const blob = await makeQuoteVideo({ ...data, size, music: audio });
      setFile(new File([blob], `quote-${slug(q)}-${size}.mp4`, { type: "video/mp4" }));
    } catch { toast.error("Couldn't make the video. Try again."); } finally { setBusy(false); }
  };
  return (
    <div className="w-full mt-2 bg-[#0B0C0E] border border-[#2A2E39] rounded-lg p-3 space-y-3" data-testid={`quote-video-panel-${q.id}`}>
      <div className="flex flex-wrap gap-2">
        <button onClick={() => { setSize("tall"); setFile(null); }} className={pick(size === "tall")} data-testid={`quote-video-tall-${q.id}`}>Tall (TikTok, Reels, Stories)</button>
        <button onClick={() => { setSize("square"); setFile(null); }} className={pick(size === "square")} data-testid={`quote-video-square-${q.id}`}>Square (FB, IG posts)</button>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <select value={music} onChange={(e) => { stop(); setMusic(e.target.value); setFile(null); }} className="bg-black border border-[#2A2E39] rounded px-3 py-1.5 text-sm text-gray-100" data-testid={`quote-video-music-${q.id}`}>
          {tracks.map((t) => <option key={t.id} value={t.id}>{t.title}</option>)}
          <option value="none">No music (add a sound in TikTok)</option>
        </select>
        {music !== "none" && (
          <button onClick={preview} className={`${btn} border-[#2A2E39] text-gray-300 flex items-center gap-1.5`} data-testid={`quote-video-preview-${q.id}`}>
            {playing ? <Square size={12} /> : <Play size={12} />} {playing ? "Stop" : "Listen"}
          </button>
        )}
      </div>
      {file ? <ReadyFile file={file} tid={`video-${q.id}`} /> : (
        <button onClick={make} disabled={busy} className="w-full hero-accent text-white text-xs font-mono-ui uppercase tracking-wider py-2.5 rounded flex items-center justify-center gap-2 disabled:opacity-60" data-testid={`quote-video-make-${q.id}`}>
          {busy ? <><Loader2 size={14} className="animate-spin" /> Making your video…</> : <><Video size={14} /> Make my video</>}
        </button>
      )}
    </div>
  );
};

const Toggle = ({ q, what, icon: Icon, open, onClick }) => (
  <button onClick={onClick} className={`flex items-center gap-1.5 ${btn} ${open ? "border-[#FD0F10] text-white" : "border-[#2A2E39] text-gray-300"} hover:border-[#FD0F10] hover:text-white`} data-testid={`quote-${what}-btn-${q.id}`}>
    <Icon size={13} /> Make a {what}
  </button>
);

// Card + video buttons for one quote; the open panel drops below the row.
export const QuoteMakers = ({ q, isPro, tracks }) => {
  const [open, setOpen] = useState("");
  if (!isPro) return <><Locked q={q} what="card" /><Locked q={q} what="video" /></>;
  const flip = (w) => setOpen(open === w ? "" : w);
  return (
    <>
      <Toggle q={q} what="card" icon={ImageDown} open={open === "card"} onClick={() => flip("card")} />
      <Toggle q={q} what="video" icon={Video} open={open === "video"} onClick={() => flip("video")} />
      {open === "card" && <CardPanel q={q} />}
      {open === "video" && <VideoPanel q={q} tracks={tracks} />}
    </>
  );
};
